import { Spin } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom'
import { Navigate } from 'react-router-dom'
import { pageUrl } from '@/constants/url'
import { api } from '@/utils/api'
import {
  apiProfile,
  apiBranchesMe,
  apiOnboardingCompanyProfile,
} from '@/utils/api/url'
import { ModalExpired } from '@/components/layout/modalExpired'
import { useUserStore } from '@/store/useUserStore'
import { useBranchStore } from '@/store/useBranchStore'
import { useOnboardingStore } from '@/store/useOnboarding'
import { CompanyProfileOnboarding } from '@/types/onboarding'
import { checkIsNeedCompleteOnboarding } from '@/utils/helpers/onboarding'
import { useRoleMe } from '@/hooks/role/useRoleMe'
import { billingPermissions } from '@/constants/permissions'
import { createRouteForAPI } from '@/utils/helpers/urlHelper'
import { useGetNotificationPermission } from '@/hooks/notification/useGetNotificationPermission'
import { useAccess } from '@/hooks/commons/useAccess'

export const PrivateRoute = () => {
  const navigate = useNavigate()
  const { branch } = useParams()
  useGetNotificationPermission()
  const { setCompanyProfileOnboarding, companyProfile } = useOnboardingStore()
  const {
    isLoading: isLoadingRoleMe,
    data: roleMe,
    isFetching,
    isError,
  } = useRoleMe()
  const location = useLocation()
  const [isLoading, setIsloading] = useState(true)
  const [isLogin, setIsLogin] = useState(true)
  const { setProfile, profile, setRolesMe } = useUserStore()
  const { setMyBranches, myBranches } = useBranchStore()
  const initialAction = useCallback(async () => {
    try {
      const [
        { data: profileData },
        { data: branchData },
        { data: companyProfile },
      ] = await Promise.all([
        api.get(apiProfile),
        api.get(apiBranchesMe),
        api.get(apiOnboardingCompanyProfile),
      ])
      setProfile(profileData)
      const safeCompanyProfile = companyProfile as CompanyProfileOnboarding
      setCompanyProfileOnboarding(safeCompanyProfile)

      if (profileData.needSetupAccount) {
        navigate(pageUrl.setupProfile)
      } else if (
        checkIsNeedCompleteOnboarding(safeCompanyProfile) &&
        !location.pathname.includes(pageUrl.onboarding)
      ) {
        navigate(pageUrl.onboarding)
      }
      setMyBranches(branchData)
      setIsLogin(true)
    } catch (error) {
      console.log(error)
    } finally {
      setIsloading(false)
    }
  }, [navigate, setProfile])
  useEffect(() => {
    initialAction()
  }, [initialAction])
  // handle onboarding process
  useEffect(() => {
    const path = location.pathname
    const isNeedCompleteOnboarding =
      checkIsNeedCompleteOnboarding(companyProfile)

    if (
      !path.includes(pageUrl.onboarding) &&
      !path.includes(pageUrl.setupProfile) &&
      profile
    ) {
      if (profile.needSetupAccount) {
        navigate(pageUrl.setupProfile)
      } else if (isNeedCompleteOnboarding) {
        navigate(pageUrl.onboarding)
      }
    }
  }, [location.pathname, profile, navigate, companyProfile])
  useEffect(() => {
    if (roleMe) {
      setRolesMe(roleMe)
    }
  }, [roleMe, setRolesMe])
  useEffect(() => {
    if (roleMe && branch && !isFetching && !isError) {
      const haveAccessBranch = roleMe.find((el) => el.branchSlug === branch)
      if (!haveAccessBranch) navigate(pageUrl.error403)
    }
  }, [branch, roleMe, navigate, isFetching, isError])

  // handle expired billing
  const { allowAccess } = useAccess()
  useEffect(() => {
    if (
      allowAccess !== undefined &&
      roleMe &&
      myBranches.length &&
      !allowAccess
    ) {
      const isExpired = allowAccess === false
      const findBillingPermission = roleMe.find((el) =>
        el.permissions.includes(billingPermissions.read),
      )
      if (findBillingPermission && isExpired) {
        navigate(
          createRouteForAPI(pageUrl.billing, {
            branch: findBillingPermission.branchSlug,
          }),
        )
      } else if (isExpired && !findBillingPermission) {
        navigate(pageUrl.error402)
      }
    }
  }, [allowAccess, myBranches.length, navigate, roleMe, location.pathname])
  if (isLoading || isLoadingRoleMe) {
    return (
      <Spin>
        <div className="h-screen" />
      </Spin>
    )
  }
  if (!isLogin) {
    return <Navigate to={pageUrl.signIn} replace />
  }
  return (
    <>
      <ModalExpired />
      <Outlet />
    </>
  )
}
